import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '../../api';

const ACCENT = '#2B7FFF';

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function formatDate(d) {
  return d.toISOString().split('T')[0];
}

function RescheduleModal({ appointment, onClose, onSuccess }) {
  const [selectedDate, setSelectedDate] = useState(formatDate(addDays(new Date(), 1)));
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const today = new Date();
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = addDays(today, i + 1);
    return { date: formatDate(d), day: d.toLocaleDateString('en-US', { weekday: 'short' }), num: d.getDate() };
  });

  const loadSlots = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setLoadingSlots(true);
    setSlots([]);
    api.get(`/availability?date=${date}&service_id=${appointment.service_id}`)
      .then(r => setSlots(r.data))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  };

  React.useEffect(() => { loadSlots(selectedDate); }, []); // eslint-disable-line

  const handleSubmit = async () => {
    if (!selectedSlot) return;
    setSubmitting(true);
    setError('');
    try {
      await api.put(`/appointments/${appointment.id}/reschedule`, {
        new_date: selectedDate,
        new_time: selectedSlot.start_time,
      });
      onSuccess(selectedDate, selectedSlot);
    } catch (err) {
      setError(err.response?.data?.error || 'Reschedule failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', overflowY: 'auto',
    }}>
      <div style={{
        background: '#0F1929', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px',
        padding: '28px 24px', maxWidth: '480px', width: '100%',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ fontWeight: 800, fontSize: '18px', textTransform: 'uppercase' }}>Reschedule</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#555', fontSize: '20px', cursor: 'pointer' }}>×</button>
        </div>

        <p style={{ color: '#888', fontSize: '13px', marginBottom: '20px' }}>
          Select a new date and time for your {appointment.service_name} appointment.
        </p>

        {/* Date picker */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '20px' }}>
          {weekDays.map(({ date, day, num }) => {
            const isSelected = selectedDate === date;
            return (
              <button key={date} onClick={() => loadSlots(date)} style={{
                padding: '10px 4px', borderRadius: '6px', border: '1px solid',
                borderColor: isSelected ? ACCENT : '#333',
                background: isSelected ? 'rgba(43,127,255,0.1)' : '#050810',
                color: isSelected ? ACCENT : '#888',
                cursor: 'pointer', textAlign: 'center',
              }}>
                <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase' }}>{day}</div>
                <div style={{ fontSize: '15px', fontWeight: 800, marginTop: '2px' }}>{num}</div>
              </button>
            );
          })}
        </div>

        {/* Time slots */}
        {loadingSlots ? (
          <div style={{ textAlign: 'center', padding: '24px' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : slots.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#555', padding: '20px', fontSize: '13px' }}>No slots available this day.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: '6px', marginBottom: '20px', maxHeight: '200px', overflowY: 'auto' }}>
            {slots.map(slot => {
              const isSelected = selectedSlot?.start_time === slot.start_time;
              return (
                <button key={slot.start_time} onClick={() => setSelectedSlot(slot)} style={{
                  padding: '10px', borderRadius: '6px', border: '1px solid',
                  borderColor: isSelected ? ACCENT : slot.is_after_hours ? 'rgba(43,127,255,0.25)' : '#333',
                  background: isSelected ? 'rgba(43,127,255,0.15)' : slot.is_after_hours ? 'rgba(43,127,255,0.04)' : '#050810',
                  color: isSelected ? ACCENT : slot.is_after_hours ? ACCENT : '#ccc',
                  cursor: 'pointer', fontWeight: 700, fontSize: '13px',
                }}>
                  {formatTime(slot.start_time)}
                </button>
              );
            })}
          </div>
        )}

        {error && (
          <div style={{ color: '#ef4444', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', padding: '10px 14px', borderRadius: '6px', fontSize: '13px', marginBottom: '14px' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={handleSubmit}
            disabled={!selectedSlot || submitting}
            className="btn btn-primary"
            style={{ flex: 1, padding: '14px', fontSize: '14px' }}
          >
            {submitting ? 'Rescheduling...' : 'Confirm Reschedule'}
          </button>
          <button onClick={onClose} className="btn btn-ghost" style={{ padding: '14px 18px', fontSize: '14px' }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BookingConfirmation() {
  const { state } = useLocation();
  const [showReschedule, setShowReschedule] = useState(false);
  const [rescheduled, setRescheduled] = useState(null);

  if (!state) return (
    <div style={{ textAlign: 'center', padding: '80px 24px' }}>
      <p style={{ color: '#888' }}>No booking data found.</p>
      <Link to="/book" className="btn btn-primary" style={{ marginTop: '24px' }}>Book Again</Link>
    </div>
  );

  const { appointment, service, slot, client, deepFacialAppt, deepFacialService } = state;
  const safeDate = rescheduled?.date || slot?.date || appointment?.appointment_date || '';
  const safeTime = rescheduled?.slot?.start_time || slot?.start_time;
  const dateObj = safeDate ? new Date(safeDate + 'T00:00:00') : null;
  const displayDate = dateObj && !isNaN(dateObj)
    ? dateObj.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
    : safeDate || 'Date not available';

  const isMobile = service.is_mobile;
  const depositRequired = appointment.deposit_required;
  const depositAmount = appointment.deposit_amount;

  return (
    <div style={{ maxWidth: '560px', margin: '60px auto', padding: '24px', textAlign: 'center' }}>
      {showReschedule && (
        <RescheduleModal
          appointment={{ ...appointment, service_name: service.name }}
          onClose={() => setShowReschedule(false)}
          onSuccess={(date, newSlot) => {
            setRescheduled({ date, slot: newSlot });
            setShowReschedule(false);
          }}
        />
      )}

      {/* Success mark */}
      <div style={{
        width: '72px', height: '72px', borderRadius: '50%',
        background: 'rgba(43,127,255,0.1)', border: '2px solid rgba(43,127,255,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto 24px', fontSize: '28px',
      }}>
        {rescheduled ? '↺' : '✓'}
      </div>

      <div style={{ color: ACCENT, fontSize: '11px', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: '10px' }}>
        {rescheduled ? 'Rescheduled' : 'Request Sent'}
      </div>
      <h1 style={{ fontSize: '32px', fontWeight: 900, letterSpacing: '-0.02em', textTransform: 'uppercase', marginBottom: '12px' }}>
        {rescheduled ? 'New Time Set' : "You're Booked"}
      </h1>
      <p style={{ color: '#888', marginBottom: '36px', lineHeight: 1.75, fontSize: '15px' }}>
        {rescheduled
          ? 'Your appointment has been rescheduled. A confirmation email has been sent.'
          : 'Christian will confirm your appointment shortly.'}
      </p>

      {/* Details card */}
      <div className="card" style={{ textAlign: 'left', marginBottom: '16px', borderColor: 'rgba(43,127,255,0.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            <div style={{ fontWeight: 900, fontSize: '18px', letterSpacing: '0.01em' }}>{service.name}</div>
            <div style={{ color: '#888', fontSize: '13px', marginTop: '3px' }}>{service.duration_minutes} min · Request #{appointment.id}</div>
            {(rescheduled?.slot?.is_after_hours ?? slot?.is_after_hours) && (
              <span className="badge badge-after-hours" style={{ marginTop: '8px', display: 'inline-block' }}>After Hours</span>
            )}
          </div>
          <div style={{ color: ACCENT, fontWeight: 900, fontSize: '24px', lineHeight: 1, letterSpacing: '-0.02em' }}>
            ${parseFloat(appointment.final_price || service.price).toFixed(0)}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {[
            { label: 'Date', value: displayDate },
            { label: 'Time', value: formatTime(safeTime) },
            { label: 'Name', value: client.name },
            { label: 'Phone', value: client.phone },
            isMobile && appointment.mobile_location ? { label: 'Location', value: appointment.mobile_location } : null,
            isMobile && appointment.mobile_event_type ? { label: 'Event', value: appointment.mobile_event_type } : null,
            isMobile && appointment.mobile_people_count ? { label: 'People', value: appointment.mobile_people_count } : null,
          ].filter(Boolean).map(({ label, value }) => (
            <div key={label} style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <span style={{ color: '#555', fontSize: '11px', width: '64px', flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
              <span style={{ fontWeight: 600, fontSize: '14px' }}>{value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Deep Facial add-on */}
      {deepFacialAppt && deepFacialService && (
        <div style={{ background: 'rgba(43,127,255,0.05)', border: '1px solid rgba(43,127,255,0.25)', borderRadius: '8px', padding: '14px 18px', marginBottom: '12px', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ color: ACCENT, fontWeight: 700, fontSize: '14px' }}>Deep Facial Add-On</div>
            <div style={{ color: '#555', fontSize: '12px', marginTop: '2px' }}>Request #{deepFacialAppt.id} · 20 min</div>
          </div>
          <div style={{ color: ACCENT, fontWeight: 800 }}>+$60</div>
        </div>
      )}

      {/* Status */}
      {!rescheduled && (
        <div style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '6px', padding: '14px 18px', marginBottom: '12px', fontSize: '13px', color: '#f59e0b' }}>
          <strong>Pending Review</strong> — Christian will confirm soon.
        </div>
      )}

      {depositRequired && !rescheduled && (
        <div style={{ background: 'rgba(43,127,255,0.06)', border: '1px solid rgba(43,127,255,0.2)', borderRadius: '6px', padding: '14px 18px', marginBottom: '12px', fontSize: '13px', color: ACCENT }}>
          <strong>50% Deposit: ${parseFloat(depositAmount || 0).toFixed(0)}</strong>
          <br /><span style={{ fontSize: '12px', color: '#888', marginTop: '3px', display: 'block' }}>Collected when Christian confirms your booking.</span>
        </div>
      )}

      {/* Cancellation */}
      <div style={{ background: '#0F1929', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '6px', padding: '14px 18px', marginBottom: '24px', fontSize: '12px', color: '#555', lineHeight: 1.8, textAlign: 'left' }}>
        <strong style={{ color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '11px' }}>Cancellation:</strong>{' '}
        3+ hours before → free. Under 3 hours → 50%. No-show → 100%.
      </div>

      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
        {!rescheduled && (
          <button
            onClick={() => setShowReschedule(true)}
            className="btn btn-outline"
            style={{ fontSize: '14px' }}
          >
            Reschedule
          </button>
        )}
        <Link to="/book" className="btn btn-primary">New Request</Link>
        <Link to="/" className="btn btn-outline">Back to Home</Link>
      </div>

      <div style={{ marginTop: '48px', paddingTop: '24px', borderTop: '1px solid rgba(255,255,255,0.05)', textAlign: 'center' }}>
        <a href="https://joinmane.com" style={{ color: '#334', fontSize: '12px', textDecoration: 'none', letterSpacing: '0.08em' }}>
          Powered by <strong style={{ color: '#2B7FFF' }}>MANE</strong>
        </a>
      </div>
    </div>
  );
}
